import { notification } from "antd";
import cuid from "cuid";
import {
  filter,
  find,
  get,
  head,
  isArray,
  isEmpty,
  isNil,
  last,
  sortBy,
} from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import format from "string-template";
import { BUILD_GENERAL_COSTS } from "utils/api/graphql/mutations/costs";
import { DOWNLOAD_SINGLE_DOCUMENT } from "utils/api/graphql/mutations/download-document";
import {
  DUPLICATE_PROJECT,
  UPDATE_PROJECT,
} from "utils/api/graphql/mutations/projects";
import { CREATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { GENERAL_COSTS } from "utils/api/graphql/queries/general-cost";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import { OFFER } from "utils/api/graphql/queries/offers";
import { PROJECT } from "utils/api/graphql/queries/projects";
import {
  ACTION_TYPES,
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  INSURANCE_PLAN_PARAMS_RULES,
  KEYS,
  PROCESS_TYPES,
  RELATIONSHIP,
  SCREENS,
  TEMPLATES,
  TEMPLATE_TYPES,
} from "utils/constants";
import { redirectAfterDuplicateProject } from "utils/helpers/project";
import { getMinDeltaStart } from "utils/helpers/array";
import { generateSQLCondition } from "utils/helpers/graphql";
import { filterByConditions } from "../../../..";
import Loader from "../Components/Spin";
import Template from "../Template";
import View from "./View";

const findStatus = (data, template) => {
  return find(data, ({ template: _ }) => _ === template);
};

const Products = ({
  processId,
  tracer,
  currentStatus,
  project,
  onNext,
  onBack,
}) => {
  const navigate = useNavigate();
  const [getProject] = useLazyQuery(PROJECT, {
    fetchPolicy: "no-cache",
    variables: { where: { id: project.id } },
  });
  const [loading, setLoading] = useState(true);
  const [refetch, setRefetch] = useState(false);
  const [refetchLoading, setRefetchLoading] = useState(false);
  const [products, setProducts] = useState();
  const [buildGeneralCosts] = useMutation(BUILD_GENERAL_COSTS);
  const [updateProject] = useMutation(UPDATE_PROJECT);
  const [generalCosts] = useLazyQuery(GENERAL_COSTS);
  const [addSubscription] = useMutation(CREATE_SUBSCRIPTION);
  const [getDeltaStart] = useLazyQuery(INSURANCE_PLANS, {
    fetchPolicy: "no-cache",
    variables: {
      where: {
        isStandalone: true,
      },
    },
  });
  const [getOffer] = useLazyQuery(OFFER);
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("DETAIL", { returnObjects: true });
  const [downloadSingleDocument] = useMutation(DOWNLOAD_SINGLE_DOCUMENT);
  const [duplicateProject] = useMutation(DUPLICATE_PROJECT);

  const duplicateProjectOnLocked = () => {
    const { insurancePlans, currentSubscription, nextQuoteStatus, ...fields } =
      project?.fields;

    const Template =
      TEMPLATES[PROCESS_TYPES.B2C].THEMES[TEMPLATE_TYPES.RADIANCE_COSA]
        .PRODUCTS;
    const status = findStatus(
      get(tracer, "flow.process.processStatuses", []),
      Template,
    );
    const actionsToExecute = filterByConditions(
      get(status, "actions", []),
      project,
    );
    const syncActions = actionsToExecute
      .filter(({ type }) => type === ACTION_TYPES.SYNC_PROCESSES)
      .map(({ args: { status } }) => ({
        id: cuid(),
        status: { id: status },
      }));
    duplicateProject({
      variables: {
        where: { id: project.id },
        data: {
          statuses: [{ id: cuid(), status: { id: status.id } }, ...syncActions],
          fields,
        },
      },
      onCompleted: ({ duplicateProject }) => {
        redirectAfterDuplicateProject({
          duplicateProject,
          tracer,
          statusId: get(status, "id"),
        });
      },
    });
  };

  const getInTouch = () => {
    return onNext({
      payload: {
        fields: {
          ...get(project, "fields"),
          hasUpsells: false,
        },
        contact: {
          id: get(project, "contact.id"),
          fields: {
            ...get(project, "contact.fields"),
            requestHelp: true,
          },
        },
      },
    });
  };

  const onSendQuote = async (insurancePlanId) => {
    const {
      data: { project },
    } = await getProject();
    const { data } = await getOffer({
      variables: {
        where: {
          AND: [
            { offerInsurancePlans: { insurancePlan: { id: insurancePlanId } } },
          ],
        },
      },
    });
    const mainOfferId = get(data, "offer.id", null);
    const { data: offer } = await getOffer({
      fetchPolicy: "no-cache",
      variables: {
        where: {
          AND: [{ id: mainOfferId }, { offerInsurancePlans: { main: false } }],
        },
      },
    });
    const hasUpsells = !!get(offer, "offer.offerInsurancePlans", [])?.length;
    const initialInsurancePlans = get(project, "fields.insurancePlans", []);
    if (initialInsurancePlans.includes(insurancePlanId)) {
      return onNext({
        payload: {
          fields: {
            ...project.fields,
            hasUpsells,
            currentSubscription: get(project, "subscriptions", []).find(
              ({ insurancePlan }) => insurancePlan.id === insurancePlanId,
            )?.id,
          },
          contact: {
            id: get(project, "contact.id"),
            fields: {
              ...get(project, "contact.fields"),
              requestHelp: false,
            },
          },
        },
      });
    }

    const status = get(
      last(
        sortBy(
          filter(
            project.statuses,
            ({ status }) => status.process.id === processId,
          ),
          "createdDate",
        ),
      ),
      "status",
    );
    const { possibleStatuses } = status;
    const [possibleStatus] = filterByConditions(
      sortBy(possibleStatuses, "order"),
      {
        ...project,
        status,
      },
    );

    addSubscription({
      variables: {
        data: {
          id: cuid(),
          project: { id: project.id },
          insurancePlan: {
            id: insurancePlanId,
          },
        },
      },
      onCompleted: ({
        addSubscription: { id: subscriptionId, insurancePlan },
      }) => {
        onNext({
          payload: {
            locked: false,
            fields: {
              ...project.fields,
              hasUpsells,
              insurancePlans: [
                ...get(project, "fields.insurancePlans", []),
                insurancePlanId,
              ],
              selectedToCompare: [
                ...get(project, "fields.selectedToCompare", []),
                insurancePlanId,
              ],
              ...(!isNil(possibleStatus?.nextStatus) && {
                nextQuoteStatus: possibleStatus.nextStatus,
              }),
              needs: get(
                head(
                  products.filter(
                    ({ insurancePlan: { id } }) => id === insurancePlanId,
                  ),
                ),
                "insurancePlan.insurancePlanCoverages",
                [],
              ).reduce((acc, current) => {
                acc[get(current, "coverageCategory.value")] =
                  get(project, "fields.selectedLevel", 1) || 1;
                return acc;
              }, {}),
              currentSubscription: subscriptionId,
            },
            contact: {
              id: get(project, "contact.id"),
              fields: {
                ...get(project, "contact.fields"),
                requestHelp: false,
              },
            },
          },
        });
      },
    });
  };

  useEffect(() => {
    getDeltaStart({
      onCompleted: ({ insurancePlans: { data: insurancePlans } }) => {
        const deltaRia = getMinDeltaStart({ insurancePlans, minValue: 30 });
        const deltaStart = getMinDeltaStart({
          insurancePlans,
          minValue: 1,
          key: "DELTA_START",
        });
        const deltaS = project.ria ? deltaRia : deltaStart;
        if (project?.expired) {
          notification.open({
            message: format(TRANSLATION.EXPIRATION.TITLE, {
              issuanceDate: moment().add(deltaS, "days").format(DATE_FORMAT),
            }),
            description: TRANSLATION.EXPIRATION.DESCRIPTION,
            duration: 5,
            icon: <HiOutlineExclamationCircle size={32} />,
            type: "error",
          });
        }
        const issuanceDate = moment()
          .add(deltaS, "days")
          .isAfter(project?.contract?.issuanceDate)
          ? moment().add(deltaS, "days").toDate()
          : project?.contract?.issuanceDate;

        updateProject({
          variables: {
            where: { id: project.id },
            data: {
              expired: false,
              contract: {
                ...project.contract,
                issuanceDate,
              },
            },
          },
          onCompleted: () => {
            const issuanceDateDifference = moment(issuanceDate)
              .startOf("day")
              .diff(moment().startOf("day"), "days");
            const adherentAge = moment(
              get(project, "contact.user.birthDate"),
            ).startOf("year");

            const age = moment().diff(adherentAge, "years");

            const [startKey, endKey] = project.ria
              ? [
                  INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA,
                  INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA,
                ]
              : [
                  INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
                  INSURANCE_PLAN_PARAMS_RULES.DELTA_END,
                ];

            const socialRegime = get(project, "contact.socialRegime");

            const zipCode = get(
              get(project, "contact.telecoms", []).find(
                ({ system }) => system === CONTACT_POINT_SYSTEM.ZIP_CODE,
              ),
              "value",
              "",
            ).toString();

            const department = zipCode.substring(0, 2);

            const profile = get(project, "contact.fields.profile");

            const sqlConditions = [
              generateSQLCondition(
                startKey,
                "r.value IS NULL OR CAST(r.value AS NUMERIC) <= :value",
                issuanceDateDifference,
              ),
              generateSQLCondition(
                endKey,
                `r.value IS NULL OR CAST(r.value AS NUMERIC) >= :value ${
                  moment(get(project, "contract.issuanceDate")).year() ===
                  moment().year()
                    ? "OR CAST(r.value AS NUMERIC) = 0"
                    : ""
                }`,
                issuanceDateDifference,
              ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.MIN_AGE,
                "r.value IS NULL OR CAST(r.value AS NUMERIC) <= :value",
                age,
              ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.MAX_AGE,
                "r.value IS NULL OR CAST(r.value AS NUMERIC) >= :value",
                age,
              ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.SOCIAL_REGIMES,
                `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
                `'${socialRegime}'`,
              ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.PROFILES,
                `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
                `'${profile}'`,
              ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.DEPARTMENTS,
                `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
                `'${department}'`,
              ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.ZIP_CODES,
                `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
                `'${zipCode}'`,
              ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.PROCESSES,
                `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
                `'${processId}'`,
              ),
            ];

            const insurancePlanFilter = {
              where: {
                isStandalone: true,
              },
              rules: sqlConditions.filter(Boolean),
              custom: {
                AND: [
                  {
                    OR: [
                      { startDate: null },
                      {
                        startDate: issuanceDate,
                        operation: "<=",
                      },
                    ],
                  },
                  {
                    OR: [
                      { endDate: null },
                      {
                        endDate: issuanceDate,
                        operation: ">=",
                      },
                    ],
                  },
                ],
              },
            };
            buildGeneralCosts({
              variables: {
                data: {
                  project: { id: project.id },
                  insurancePlanFilter,
                },
              },
              onCompleted: ({ buildGeneralCosts }) => {
                const ids = buildGeneralCosts
                  .filter(({ cost }) => cost)
                  .map(({ id }) => id);
                generalCosts({
                  fetchPolicy: "no-cache",
                  variables: {
                    isIn: { id: ids },
                    take: 4,
                    lt: { cost: +get(project, "fields.budget.max") },
                    gte: { cost: +get(project, "fields.budget.min") },
                  },
                  onCompleted: ({ generalCosts }) => {
                    const productsData = omitDeep(
                      get(generalCosts, "data", []),
                      "__typename",
                    );
                    const [recommendedProduct] = productsData.splice(0, 1);
                    if (recommendedProduct)
                      productsData.splice(1, 0, recommendedProduct);
                    setProducts(productsData);
                    setLoading(false);
                    setRefetchLoading(false);
                  },
                });
              },
            });
          },
        });
      },
    });
  }, [refetch]);

  const refetchCosts = async ({ contact }) => {
    setRefetchLoading(true);
    const relatedPersons = get(contact, "relatedPersons", []).filter((_) => _);
    const hasChildren = !!relatedPersons.filter(
      ({ relationship }) => relationship === RELATIONSHIP.CHILD,
    ).length;
    const hasSpouse = !isEmpty(
      relatedPersons.find(
        ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
      ),
    );

    await updateProject({
      variables: {
        where: { id: project.id },
        data: {
          contact,
          fields: {
            ...get(project, "fields"),
            hasRelatedPersons: {
              hasChildren,
              hasSpouse,
            },
          },
        },
      },
      onCompleted: () => {
        setRefetch((prev) => !prev);
      },
    });
  };
  if (loading || !isArray(products)) return <Loader />;

  return (
    <Template
      onBack={onBack}
      progress={currentStatus.progress}
      loading={loading}
      project={project}
      View={
        <View
          products={products}
          onSendQuote={onSendQuote}
          contact={get(project, "contact", {})}
          refetchCosts={refetchCosts}
          downloadSingleDocument={downloadSingleDocument}
          locked={project.locked}
          duplicateProject={duplicateProjectOnLocked}
          getInTouch={getInTouch}
          onBack={onBack}
          refetchLoading={refetchLoading}
        />
      }
    />
  );
};

export default Products;
